import { Box, Divider, Grid } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { GetImageQueriesExamplesParams, useImageQueriesExamples } from "api/services/useImageQueriesExamples";
import { getCanvasAnnotationsFromROIs } from "features/canvas/utils";
import { ExampleImageThumbnail } from "features/reviewPageContent/components/ExampleImageThumbnail";
import { AnswerBar } from "new-components/answerBar/AnswerBar";
import { Typography } from "new-components/typography/Typography";
import { DetectorModes } from "types/createDetector";
import { ImageQuery, Posicheck } from "types/imageQueries";
import { getTitleSuffix } from "utils/imageQueriesUtils";

import { ExampleImageModal } from "./ExampleImageModal";

// Check if an example should be excluded (is awaiting answer or is a placeholder)
const shouldExcludeExample = (example: ImageQuery) => 
  example?.is_placeholder || (example?.in_queue && !example?.current_best_answer_source);

interface ExamplePanelProps {
  currentPosicheck: Posicheck;
}

export enum EXAMPLE_CHOICES {
  YES = "YES",
  NO = "NO",
  HAS_TEXT = "HAS_TEXT",
  NO_TEXT = "NO_TEXT",
  COUNT = "COUNT",
  BOUNDING_BOX = "BOUNDING_BOX",
  NO_OBJECTS = "NO_OBJECTS",
  GREATER_THAN_MAX = "GREATER_THAN_MAX",
}

export function ExamplesPanel({ currentPosicheck }: ExamplePanelProps) {
  const {
    id: iqId,
    predictor: { id: predictorId, mode: predictorMode, mode_configuration: predictorModeConfiguration },
  } = currentPosicheck;

  const [showExampleModal, setShowExampleModal] = useState<boolean>(false);
  const [selectedExampleImageQuery, setSelectedExampleImageQuery] = useState<ImageQuery | null>(null);
  const [selectedClassExamples, setSelectedClassExamples] = useState<ImageQuery[]>([]);
  const [choiceExamples, setChoiceExamples] = useState<{ [key: string]: ImageQuery[] }>({});
  const [isLoadingExamples, setIsLoadingExamples] = useState(false);

  const { fetchImageQueriesExamples } = useImageQueriesExamples();

  const currentChoices = useMemo(() => {
    if (predictorMode === DetectorModes.BINARY) return [EXAMPLE_CHOICES.YES, EXAMPLE_CHOICES.NO];
    if (predictorMode === DetectorModes.TEXT_RECOGNITION) return [EXAMPLE_CHOICES.HAS_TEXT, EXAMPLE_CHOICES.NO_TEXT];
    if (predictorMode === DetectorModes.BOUNDING_BOX) return [EXAMPLE_CHOICES.BOUNDING_BOX, EXAMPLE_CHOICES.NO_OBJECTS];
    if (predictorMode === DetectorModes.MULTICHOICE)
      return predictorModeConfiguration?.class_names || [];
    if (predictorMode === DetectorModes.COUNT) return [EXAMPLE_CHOICES.COUNT];
    return [];
  }, [predictorMode, predictorModeConfiguration]);

  const getAmountOfExamples = () => {
    if ([DetectorModes.BINARY, DetectorModes.TEXT_RECOGNITION].includes(predictorMode)) {
      return 3;
    }
    if (predictorMode === DetectorModes.COUNT) {
      return 25;
    }
    return 1;
  };

  const getAmountOfColumns = () => {
    if (
      predictorMode === DetectorModes.COUNT ||
      (predictorMode === DetectorModes.MULTICHOICE && currentChoices.length > 2)
    ) {
      return 2;
    }
    return 1;
  };

  const getChoiceExamples = useCallback(async () => {
    if (currentPosicheck) {
      const exampleCount = getAmountOfExamples();
      const examplesPerChoice: { [key: string]: ImageQuery[] } = {};
      setIsLoadingExamples(true);

      const promises = currentChoices.map(async (choice: string) => {
        const response = await fetchImageQueriesExamples({
          label: choice,
          predictorId,
          currentImageQueryId: iqId,
          pageSize: exampleCount,
        });
        // Filter out placeholders and examples that are awaiting answer (shows wait icon)
        const filteredResults = (response?.results || []).filter(
          (example) => !shouldExcludeExample(example)
        );
        examplesPerChoice[choice] = filteredResults;
        return response;
      });

      await Promise.all(promises);

      // Create ordered object based on currentChoices order and pad with nulls if needed
      const orderedExamples: { [key: string]: ImageQuery[] } = {};
      currentChoices.forEach((choice) => {
        const examples = examplesPerChoice[choice] || [];
        // Pad with nulls if we have fewer examples than exampleCount
        let paddedExamples = [...examples];
        while (predictorMode !== DetectorModes.COUNT && paddedExamples.length < exampleCount) {
          let placeholder = choice;
          if (predictorMode === DetectorModes.TEXT_RECOGNITION) {
            placeholder = placeholder.replace("HAS_TEXT", "HAS TEXT").replace("NO_TEXT", "NO TEXT");
          }
          paddedExamples.push({ current_best_prediction_str: placeholder } as ImageQuery);
        }
        if (predictorMode === DetectorModes.COUNT) {
          // Move examples with ">" to the end of the list
          const filteredExamples = paddedExamples.filter((example) =>
            example.current_best_prediction_str?.startsWith(">")
          );
          paddedExamples = paddedExamples.filter((example) => !example.current_best_prediction_str?.startsWith(">"));
          paddedExamples = [...paddedExamples, ...filteredExamples];
        }
        orderedExamples[choice] = paddedExamples;
      });

      setChoiceExamples(orderedExamples);
      setIsLoadingExamples(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPosicheck, fetchImageQueriesExamples]);

  useEffect(() => {
    getChoiceExamples();
    return () => {
      setChoiceExamples({});
    };
  }, [getChoiceExamples]);

  const getRowHeight = (examples: ImageQuery[]) => {
    const rows = Math.ceil(examples.length / getAmountOfColumns());
    if (rows > 3) {
      return "h-[28%]";
    }
    if (rows === 3) {
      return `h-[calc(33.33%)]`;
    }
    if (rows === 2) {
      return "h-[calc(50%)]";
    }
    return "h-[100%]";
  };

  const handleClose = () => {
    setShowExampleModal(false);
    setSelectedExampleImageQuery(null);
    setSelectedClassExamples([]);
  };

  const handleOpen = async (example: ImageQuery) => {
    setIsLoadingExamples(true);
    try {
      // Determine the correct label for the API based on detector mode
      let label = example.current_best_prediction_str || "";
      let countValue = "";
      if (predictorMode === DetectorModes.COUNT) {
        // For COUNT detectors, use "COUNT" label
        label = "COUNT";
        const answer = example.last_label?.answer;
        countValue = answer && typeof answer === "object" && "value" in answer ? String(answer.value) : "";
      } else if (predictorMode === DetectorModes.TEXT_RECOGNITION) {
        // For TEXT_RECOGNITION, normalize "HAS TEXT" to "HAS_TEXT" and "NO TEXT" to "NO_TEXT"
        label = !label || label.toUpperCase() === "UNCLEAR" ? "NO_TEXT" : "HAS_TEXT";
      }

      const params: GetImageQueriesExamplesParams = {
        label,
        predictorId,
        currentImageQueryId: iqId, // Use current posicheck's ID to exclude it, not the clicked example
        pageSize: 10,
      };

      if (predictorMode === DetectorModes.COUNT && countValue) {
        params.countValue = countValue;
      }

      const response = await fetchImageQueriesExamples(params);
      // Filter out placeholders and examples that are awaiting answer (shows wait icon)
      const classExamples = response?.results?.filter(
        (ex) => ex?.iq_id && !shouldExcludeExample(ex)
      ) || [];
      
      // Reorder so the clicked example appears first in the modal
      const clickedExampleIndex = classExamples.findIndex((ex) => ex.iq_id === example.iq_id);
      const orderedExamples = [...classExamples];
      if (clickedExampleIndex > 0) {
        // Move the clicked example to the front
        const [clickedExample] = orderedExamples.splice(clickedExampleIndex, 1);
        orderedExamples.unshift(clickedExample);
      } else if (clickedExampleIndex === -1) {
        // The clicked example isn't in the fetched results, add it at the beginning
        orderedExamples.unshift(example);
      }
      // If clickedExampleIndex === 0, it's already first, no change needed
      
      setSelectedClassExamples(orderedExamples);
      setSelectedExampleImageQuery(example);
      setShowExampleModal(true);
    } catch (error) {
      console.error("Failed to fetch class examples:", error);
    } finally {
      setIsLoadingExamples(false);
    }
  };

  const getExamplesList = (examples: ImageQuery[]) => (
    <Box className="h-[calc(100%-21px)] w-full">
      {examples ? (
        <Grid container columnSpacing="10px" height="100%" overflow="auto">
          {examples.map((example, index) => {
            const canvasRois = getCanvasAnnotationsFromROIs(example.current_best_prediction_rois || []);
            return (
              <Grid
                item
                key={`example-${example?.iq_id || index}`}
                xs={12 / getAmountOfColumns()}
                className={`${getRowHeight(examples)} pt-[10px]`}
              >
                <Box className="h-[calc(100%-24px)] w-full" data-testid="example-thumbnail-container">
                  {example?.iq_id ? (
                    <ExampleImageThumbnail
                      exampleImageQuery={example}
                      handleOpen={() => handleOpen(example)}
                      canvasRois={canvasRois}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                        height: "100%",
                        justifyContent: "center",
                        alignItems: "center",
                        backgroundColor: "rgba(0, 0, 0, 0.11)",
                      }}
                    >
                      <Typography
                        data-testid="example-thumbnail-unavailable"
                        variant="small"
                        fontWeight="bold"
                        color="text.secondary"
                        align="center"
                        className="mx-[25px]"
                      >
                        Unavailable
                      </Typography>
                    </Box>
                  )}
                </Box>
                <AnswerBar
                  imageQuery={example}
                  showTimestamp={false}
                  showConfidence={false}
                  answer={example?.current_best_prediction_str || ""}
                  small
                  titleSuffix={getTitleSuffix(example, predictorMode)}
                />
              </Grid>
            );
          })}
        </Grid>
      ) : (
        <Box className="h-full w-full flex justify-center items-center">
          <Typography variant="detail" fontWeight="bold" align="center" className="text-[#91959b]">
            No examples are available yet.
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box className="hidden md:flex h-full w-full p-[15px] rounded-[20px]" sx={{ backgroundColor: "#E5E9ED" }}>
      {!isLoadingExamples && (
        <Box height="100%" width="100%" className="flex" gap="10px">
          {predictorMode === DetectorModes.BINARY && (
            <>
              <Box display="flex" flexDirection="column" justifyContent="center" height="100%" width="50%">
                <Typography align="center" className="text-[#6250DA]">
                  <span className="font-bold">YES</span> Examples
                </Typography>
                <Divider sx={{ bgcolor: "#6250DA" }} />
                {getExamplesList(choiceExamples.YES)}
              </Box>
              <Box display="flex" flexDirection="column" justifyContent="center" height="100%" width="50%">
                <Typography align="center" className="text-[#A02BDF]">
                  <span className="font-bold">NO</span> Examples
                </Typography>
                <Divider sx={{ bgcolor: "#A02BDF" }} />
                {getExamplesList(choiceExamples.NO)}
              </Box>
            </>
          )}
          {predictorMode === DetectorModes.TEXT_RECOGNITION && (
            <>
              <Box display="flex" flexDirection="column" justifyContent="center" height="100%" width="50%">
                <Typography align="center" className="text-[#529fe7]">
                  <span className="font-bold">HAS TEXT</span> Examples
                </Typography>
                <Divider sx={{ bgcolor: "#529fe7" }} />
                {getExamplesList(choiceExamples.HAS_TEXT)}
              </Box>
              <Box display="flex" flexDirection="column" justifyContent="center" height="100%" width="50%">
                <Typography align="center" className="text-[#0352b9]">
                  <span className="font-bold">NO TEXT</span> Examples
                </Typography>
                <Divider sx={{ bgcolor: "#0352b9" }} />
                {getExamplesList(choiceExamples.NO_TEXT)}
              </Box>
            </>
          )}
          {currentPosicheck.predictor?.mode === DetectorModes.BOUNDING_BOX && (
            <>
              <Box display="flex" flexDirection="column" justifyContent="center" height="100%" width="50%">
                <Typography align="center" className="text-[#529fe7]">
                  <span className="font-bold">Bounding boxes</span> Examples
                </Typography>
                <Divider sx={{ bgcolor: "#529fe7" }} />
                {getExamplesList(choiceExamples.BOUNDING_BOX)}
              </Box>
              <Box display="flex" flexDirection="column" justifyContent="center" height="100%" width="50%">
                <Typography align="center" className="text-[#0352b9]">
                  <span className="font-bold">No objects</span> Examples
                </Typography>
                <Divider sx={{ bgcolor: "#0352b9" }} />
                {getExamplesList(choiceExamples.NO_OBJECTS)}
              </Box>
            </>
          )}
          {[DetectorModes.MULTICHOICE, DetectorModes.COUNT].includes(currentPosicheck.predictor?.mode) && (
            <Box display="flex" flexDirection="column" justifyContent="center" height="100%" width="100%">
              <Typography align="center" className="text-[#49494D]">
                Examples
              </Typography>
              <Divider sx={{ bgcolor: "#49494D" }} />
              {getExamplesList(Object.values(choiceExamples).reduce((acc, curr) => acc.concat(curr), []))}
            </Box>
          )}
        </Box>
      )}

      {selectedExampleImageQuery && (
        <ExampleImageModal
          currentPosicheck={currentPosicheck}
          exampleTitle={selectedExampleImageQuery.current_best_prediction_str || ""}
          isOpen={showExampleModal}
          onClose={handleClose}
          exampleImageQuery={selectedExampleImageQuery}
          examples={selectedClassExamples}
        />
      )}
    </Box>
  );
}
